import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Users, Eye, CheckCircle2 } from "lucide-react";
import { getVagasInfo } from "./VagasCounter";

const rand = (a, b) => a + Math.floor(Math.random() * (b - a + 1));

const plural = (k, one, many) => `${k} ${k === 1 ? one : many}`;

// Mensagens sempre coerentes com o contador: compras "agora" / "últimos minutos" = variação real do contador
const MESSAGES = [
  () => ({ Icon: Eye, text: `${rand(28, 64)} pessoas estão vendo a oferta agora` }),
  ({ sinceLast }) => sinceLast >= 1 && { Icon: CheckCircle2, text: `${plural(sinceLast, "pessoa adquiriu", "pessoas adquiriram")} a vaga vitalícia agora` },
  ({ sold }) => sold >= 2 && { Icon: CheckCircle2, text: `${sold} pessoas já garantiram a vaga vitalícia` },
  ({ remaining }) => remaining > 1 && { Icon: Users, text: `${Math.min(rand(3, 9), remaining)} pessoas adquirindo a vaga vitalícia agora` },
  () => ({ Icon: Eye, text: `${rand(118, 187)} pessoas estão olhando essa oferta agora` }),
  ({ recent }) => recent >= 2 && { Icon: CheckCircle2, text: `${recent} pessoas adquiriram nos últimos minutos` },
  ({ remaining }) => remaining > 0 && remaining <= 10 && { Icon: Users, text: `Restam apenas ${remaining} vagas nesta turma` },
  ({ remaining }) => remaining === 0 && { Icon: CheckCircle2, text: "Turma lotada: todas as 160 vagas foram preenchidas" },
];

const WINDOW = 3 * 60 * 1000;

const pick = (n, info) => {
  for (let i = 0; i < MESSAGES.length; i += 1) {
    const m = MESSAGES[(n + i) % MESSAGES.length](info);
    if (m) return m;
  }
  return MESSAGES[0]();
};

export const SocialProof = () => {
  const [msg, setMsg] = useState(null);
  const [n, setN] = useState(0);
  const samples = useRef([{ t: Date.now(), sold: getVagasInfo().sold }]);
  const lastShownSold = useRef(getVagasInfo().sold);

  useEffect(() => {
    const id = setInterval(() => {
      const now = Date.now();
      samples.current = [...samples.current.filter((s) => now - s.t <= WINDOW), { t: now, sold: getVagasInfo().sold }];
    }, 2000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const first = n === 0 ? 7000 : rand(14000, 22000);
    const show = setTimeout(() => {
      const info = getVagasInfo();
      const oldest = samples.current[0]?.sold ?? info.sold;
      const ctx = { ...info, recent: info.sold - oldest, sinceLast: info.sold - lastShownSold.current };
      const m = pick(n, ctx);
      lastShownSold.current = info.sold;
      setMsg({ ...m, key: n });
    }, first);
    const hide = setTimeout(() => {
      setMsg(null);
      setN((v) => v + 1);
    }, first + 5200);
    return () => {
      clearTimeout(show);
      clearTimeout(hide);
    };
  }, [n]);

  return (
    <div className="pointer-events-none fixed bottom-4 left-4 right-4 z-50 flex sm:right-auto">
      <AnimatePresence>
        {msg && (
          <motion.div
            key={msg.key}
            data-testid="social-proof-popup"
            initial={{ opacity: 0, y: 16, filter: "blur(4px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, y: 10, filter: "blur(4px)" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="pointer-events-auto flex max-w-sm items-center gap-3 rounded-2xl border border-white/15 bg-[#0b1510]/85 px-4 py-3 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.6)] backdrop-blur-xl"
          >
            <span className="relative grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#d9b45a]/15 text-[var(--gold)]">
              <msg.Icon size={16} />
              <span className="absolute right-0 top-0 h-2 w-2 rounded-full bg-emerald-400 ring-2 ring-[var(--night)]" />
            </span>
            <div>
              <p data-testid="social-proof-text" className="text-[13px] leading-snug text-[var(--cream)]">{msg.text}</p>
              <p className="mt-0.5 text-[11px] text-[var(--cream)]/60">Oferta Black Friday · agora</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
